import { useEffect, useState } from "react";
import { Trash2, Sparkles, Pin, PinOff, Edit3, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function MemoryItem({ m, onUpdate, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [note, setNote] = useState(m.note || "");

    const saveNote = async () => {
        try {
            const { data } = await api.patch(`/memories/${m.id}`, { note });
            onUpdate(data);
            setEditing(false);
            toast.success("note saved");
        } catch {
            toast.error("couldn't save note");
        }
    };

    const togglePin = async () => {
        try {
            const { data } = await api.patch(`/memories/${m.id}`, { pinned: !m.pinned });
            onUpdate(data);
        } catch {
            toast.error("couldn't pin");
        }
    };

    return (
        <li
            data-testid={`memory-item-${m.id}`}
            className={`group flex items-start gap-4 border rounded-2xl px-5 py-4 transition-colors ${
                m.pinned ? "bg-clay/60 border-terracotta/40" : "bg-white/70 hover:bg-white border-clayHover"
            }`}
        >
            <Sparkles className="w-4 h-4 text-terracotta mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="font-journal text-ink leading-relaxed">{m.summary}</p>
                {!editing && m.note && (
                    <p className="mt-2 px-3 py-2 bg-bone/60 border-l-2 border-terracotta/40 text-sm text-inkSoft font-body italic leading-relaxed">
                        {m.note}
                    </p>
                )}
                {editing && (
                    <div className="mt-3">
                        <textarea
                            data-testid={`memory-note-input-${m.id}`}
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={2}
                            placeholder="add a note (for you and pax)…"
                            className="w-full bg-bone/60 border border-clayHover rounded-xl p-3 outline-none focus:border-terracotta resize-none font-body text-sm text-ink"
                        />
                        <div className="flex gap-2 mt-2">
                            <button
                                data-testid={`memory-note-save-${m.id}`}
                                onClick={saveNote}
                                className="text-xs px-3 py-1.5 rounded-full bg-terracotta text-bone hover:bg-terracottaHover font-body"
                            >
                                save
                            </button>
                            <button
                                onClick={() => { setEditing(false); setNote(m.note || ""); }}
                                className="text-xs px-3 py-1.5 rounded-full text-inkSoft hover:bg-clayHover font-body"
                            >
                                cancel
                            </button>
                        </div>
                    </div>
                )}
                <p className="text-xs text-inkSoft mt-2 font-body">
                    {new Date(m.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    {m.pinned && <span className="ml-2 text-terracotta">· pinned</span>}
                </p>
            </div>
            <div className="flex flex-col gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <button
                    data-testid={`memory-pin-${m.id}`}
                    onClick={togglePin}
                    className={`${m.pinned ? "text-terracotta" : "text-inkSoft"} hover:text-terracotta`}
                    aria-label={m.pinned ? "Unpin" : "Pin"}
                >
                    {m.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                </button>
                <button
                    data-testid={`memory-edit-${m.id}`}
                    onClick={() => setEditing(e => !e)}
                    className="text-inkSoft hover:text-ink"
                    aria-label="Edit note"
                >
                    {editing ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                </button>
                <button
                    data-testid={`memory-delete-${m.id}`}
                    onClick={() => onDelete(m.id)}
                    className="text-inkSoft hover:text-danger"
                    aria-label="Forget this"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </li>
    );
}

export default function Memories() {
    const [memories, setMemories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmClear, setConfirmClear] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/memories");
            setMemories(r.data);
        } catch {
            toast.error("couldn't load memories");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const removeOne = async (id) => {
        try {
            await api.delete(`/memories/${id}`);
            setMemories(prev => prev.filter(m => m.id !== id));
            toast.success("forgotten");
        } catch {
            toast.error("couldn't remove");
        }
    };

    const updateOne = (updated) => {
        setMemories(prev => prev.map(m => m.id === updated.id ? updated : m));
    };

    const clearAll = async () => {
        try {
            const { data } = await api.delete("/memories");
            setMemories([]);
            toast.success(`forgot ${data.deleted ?? 0} ${data.deleted === 1 ? "memory" : "memories"}`);
            setConfirmClear(false);
        } catch {
            toast.error("couldn't clear");
        }
    };

    // Pinned first
    const sorted = [...memories].sort((a, b) => {
        if ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) !== 0) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
        return new Date(b.created_at) - new Date(a.created_at);
    });

    return (
        <div className="max-w-3xl mx-auto">
            <div className="flex items-start justify-between mb-2 gap-4">
                <div>
                    <h1 className="font-heading text-4xl tracking-tight text-ink">what pax remembers</h1>
                    <p className="text-inkSoft font-body mt-2 leading-relaxed">
                        a small note is kept after each conversation. pin the ones that feel important, add your own notes, or forget anything that no longer fits.
                    </p>
                </div>
                {memories.length > 0 && (
                    <button
                        data-testid="memories-clear-btn"
                        onClick={() => setConfirmClear(true)}
                        className="text-sm text-inkSoft hover:text-danger font-body whitespace-nowrap"
                    >
                        forget everything
                    </button>
                )}
            </div>

            {confirmClear && (
                <div className="my-6 bg-clay/80 border border-clayHover rounded-2xl p-5 flex items-center justify-between gap-4">
                    <p className="font-body text-ink">forget all {memories.length} memories? this can't be undone.</p>
                    <div className="flex gap-2">
                        <button onClick={() => setConfirmClear(false)} className="px-4 py-2 rounded-full text-inkSoft hover:bg-clayHover font-body">cancel</button>
                        <button data-testid="memories-clear-confirm" onClick={clearAll} className="px-4 py-2 rounded-full bg-danger text-bone hover:opacity-90 font-body">yes, forget all</button>
                    </div>
                </div>
            )}

            <div className="mt-8">
                {loading && <p className="text-inkSoft font-body italic">loading…</p>}
                {!loading && memories.length === 0 && (
                    <div className="bg-white/70 border border-clayHover rounded-3xl p-10 text-center">
                        <Sparkles className="w-6 h-6 text-terracotta mx-auto mb-3" />
                        <p className="font-journal italic text-ink mb-2 text-lg">no memories yet.</p>
                        <p className="text-inkSoft font-body text-sm">once you've had a conversation or two, a few quiet notes will live here.</p>
                    </div>
                )}

                <ul className="space-y-3" data-testid="memories-list">
                    {sorted.map(m => (
                        <MemoryItem key={m.id} m={m} onUpdate={updateOne} onDelete={removeOne} />
                    ))}
                </ul>
            </div>
        </div>
    );
}
